import { Component } from '@theme/component';
import { requestIdleCallback } from '@theme/utilities';
import { updateAddToCartPriceLabels } from '@theme/add-to-cart-price';

/**
 * Linear inches quantity calculator for Pencil Trim products.
 * Mirrors the square footage QuantityCalculator but operates in linear inches,
 * deriving pieces from the `custom.piece` metafield (lin.in per piece).
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} linFtInput
 * @property {HTMLInputElement} unitsInput
 * @property {HTMLElement} totalUnitsDisplay
 * @property {HTMLElement} totalCoverageDisplay
 * @property {HTMLElement} [linFtLabel]
 * @property {HTMLElement} [unitsLabel]
 * @property {HTMLElement} [overageDropdown]
 * @property {HTMLButtonElement} [dropdownTrigger]
 * @property {HTMLElement} [dropdownMenu]
 * @property {HTMLElement} [dropdownValue]
 * @property {HTMLElement} [overageDisplay]
 *
 * @extends {Component<Refs>}
 */
class QuantityCalculatorPencilTrim extends Component {
  /** linear inches per piece (from product metafield custom.piece). @type {number} */
  covers = 0;

  /** Overage percentage. @type {number} */
  overage = 0;

  /** Maximum quantity available. @type {number} */
  maxQuantity = 0;

  /** External error element (sibling). @type {HTMLElement | null} */
  errorElement = null;

  /** @type {boolean} */
  #isUpdating = false;

  /** Original lin.in value before overage, used to recalculate on overage change. @type {number} */
  #originalLinFt = 0;

  connectedCallback() {
    super.connectedCallback();
    requestIdleCallback(() => {
      this.#init();
    });
  }

  #init() {
    if (!this.refs.linFtInput) return;

    const linFtInput = /** @type {HTMLInputElement} */ (this.refs.linFtInput);
    const unitsInput = /** @type {HTMLInputElement} */ (this.refs.unitsInput);

    this.covers = parseFloat(linFtInput?.dataset?.covers || '0');
    this.maxQuantity = parseFloat(this.dataset?.maxQuantity || '0');

    this.errorElement = this.nextElementSibling;
    if (this.errorElement && !this.errorElement.classList.contains('quantity-calculator__error')) {
      this.errorElement = null;
    }

    linFtInput.addEventListener('input', (e) => {
      if (this.#isUpdating) return;
      this.#validateAndFormatInput(e, true);
      this.#handleLinFtChange(e);
    });

    unitsInput.addEventListener('input', (e) => {
      if (this.#isUpdating) return;
      this.#validateAndFormatInput(e, false);
      this.#handleUnitsChange(e);
    });

    this.#setupDropdown();

    // If covers is known, default to 1 piece so the lin.in field pre-fills on load
    const existingUnits = parseFloat(unitsInput?.value) || 0;
    if (this.covers > 0 && existingUnits > 0) {
      const mockEvent = { target: unitsInput };
      this.#handleUnitsChange(/** @type {Event} */ (/** @type {unknown} */ (mockEvent)));
    } else {
      this.#clearCalculations();
    }
  }

  #setupDropdown() {
    const { dropdownTrigger, dropdownMenu, overageDropdown } = this.refs;
    if (!dropdownTrigger || !dropdownMenu) return;

    dropdownTrigger.addEventListener('click', (e) => {
      e.stopPropagation();
      this.#toggleDropdown();
    });

    dropdownMenu.querySelectorAll('.quantity-calculator__dropdown-item').forEach((item) => {
      item.addEventListener('click', (e) => {
        const button = /** @type {HTMLButtonElement} */ (e.currentTarget);
        this.#selectOption(button.dataset.value || '0', button.textContent?.trim() || 'No overage');
        this.#closeDropdown();
      });
    });

    document.addEventListener('click', (e) => {
      if (overageDropdown && !overageDropdown.contains(/** @type {Node} */ (e.target))) {
        this.#closeDropdown();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.#closeDropdown();
    });
  }

  #toggleDropdown() {
    const isExpanded = this.refs.dropdownTrigger?.getAttribute('aria-expanded') === 'true';
    isExpanded ? this.#closeDropdown() : this.#openDropdown();
  }

  #openDropdown() {
    this.refs.dropdownTrigger?.setAttribute('aria-expanded', 'true');
    this.refs.dropdownMenu?.setAttribute('aria-hidden', 'false');
  }

  #closeDropdown() {
    this.refs.dropdownTrigger?.setAttribute('aria-expanded', 'false');
    this.refs.dropdownMenu?.setAttribute('aria-hidden', 'true');
  }

  #selectOption(value, text) {
    if (this.refs.dropdownValue) this.refs.dropdownValue.textContent = text;

    this.refs.dropdownMenu?.querySelectorAll('.quantity-calculator__dropdown-item').forEach((item) => {
      const btn = /** @type {HTMLButtonElement} */ (item);
      if (btn.dataset.value === value) {
        btn.setAttribute('aria-selected', 'true');
      } else {
        btn.removeAttribute('aria-selected');
      }
    });

    this.#setOverage(parseFloat(value));
  }

  /**
   * @param {Event} event
   * @param {boolean} allowDecimals
   */
  #validateAndFormatInput(event, allowDecimals) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    let value = input.value.replace(/,/g, '.');

    if (allowDecimals) {
      const cleaned = value.replace(/[^0-9.]/g, '');
      const parts = cleaned.split('.');
      if (parts.length > 2) {
        value = parts[0] + '.' + parts.slice(1).join('');
      } else if (parts.length === 2 && parts[1] && parts[1].length > 4) {
        value = parts[0] + '.' + parts[1].substring(0, 4);
      } else {
        value = cleaned;
      }
    } else {
      value = value.replace(/[^0-9]/g, '');
    }

    if (input.value !== value) input.value = value;
  }

  updatedCallback() {
    super.updatedCallback?.();
    const linFtInput = /** @type {HTMLInputElement} */ (this.refs.linFtInput);
    this.covers = parseFloat(linFtInput?.dataset?.covers || '0');
    this.#clearCalculations();
  }

  /** @param {Event} event */
  #handleLinFtChange(event) {
    const inputValue = /** @type {HTMLInputElement} */ (event.target).value;
    const linFtValue = parseFloat(inputValue) || 0;

    if (linFtValue === 0 || inputValue === '' || inputValue === '0') {
      this.#clearCalculations();
      this.#isUpdating = true;
      /** @type {HTMLInputElement} */ (this.refs.unitsInput).value = '';
      /** @type {HTMLInputElement} */ (this.refs.linFtInput).value = '';
      this.#isUpdating = false;
      this.#originalLinFt = 0;
      return;
    }

    const originalLinFt = this.overage > 0 ? linFtValue / (1 + this.overage / 100) : linFtValue;
    this.#originalLinFt = originalLinFt;

    const piecesNeeded = this.covers > 0 ? Math.ceil(linFtValue / this.covers) : 0;

    const unitsInput = /** @type {HTMLInputElement} */ (this.refs.unitsInput);
    const newUnitsValue = String(piecesNeeded);

    this.#isUpdating = true;
    unitsInput.value = newUnitsValue;
    unitsInput.setAttribute('value', newUnitsValue);
    unitsInput.dispatchEvent(new Event('input', { bubbles: true }));
    this.#isUpdating = false;

    if (this.refs.unitsLabel) {
      this.refs.unitsLabel.textContent = piecesNeeded === 1 ? 'piece' : 'pieces';
    }

    const totalCoverage = piecesNeeded * this.covers;
    this.#validateQuantity(piecesNeeded);
    this.#updateDisplays(piecesNeeded, totalCoverage, originalLinFt, linFtValue);
  }

  /** @param {Event} event */
  #handleUnitsChange(event) {
    const inputValue = /** @type {HTMLInputElement} */ (event.target).value;
    const unitsValue = parseFloat(inputValue) || 0;

    if (this.refs.unitsLabel && inputValue && inputValue !== '0') {
      this.refs.unitsLabel.textContent = unitsValue === 1 ? 'piece' : 'pieces';
    }

    if (unitsValue === 0 || inputValue === '' || inputValue === '0') {
      this.#clearCalculations();
      this.#isUpdating = true;
      /** @type {HTMLInputElement} */ (this.refs.linFtInput).value = '';
      /** @type {HTMLInputElement} */ (this.refs.unitsInput).value = '';
      this.#isUpdating = false;
      this.#originalLinFt = 0;
      return;
    }

    const totalCoverage = unitsValue * this.covers;
    const originalLinFt = this.overage > 0 ? totalCoverage / (1 + this.overage / 100) : totalCoverage;
    const linFtWithOverage = totalCoverage;
    this.#originalLinFt = originalLinFt;

    const linFtInput = /** @type {HTMLInputElement} */ (this.refs.linFtInput);
    const newValue = String(Math.round(linFtWithOverage * 100) / 100);

    this.#isUpdating = true;
    linFtInput.value = newValue;
    linFtInput.setAttribute('value', newValue);
    this.#isUpdating = false;

    this.#validateQuantity(unitsValue);
    this.#updateDisplays(unitsValue, totalCoverage, originalLinFt, linFtWithOverage);
  }

  /** @param {number} units */
  #validateQuantity(units) {
    if (!this.errorElement) return;
    this.errorElement.style.display = this.maxQuantity > 0 && units > this.maxQuantity ? 'block' : 'none';
  }

  /** @param {number} units */
  #updateAddToCartPrice(units) {
    updateAddToCartPriceLabels(units);
  }

  /**
   * @param {number} units
   * @param {number} coverage
   * @param {number} originalLinFt
   * @param {number} linFtWithOverage
   */
  #updateDisplays(units, coverage, originalLinFt, linFtWithOverage) {
    const unitText = units === 1 ? 'piece' : 'pieces';
    const coversText = this.covers > 0 ? ` (${this.covers} lin.in each)` : '';

    /** @type {HTMLElement} */ (this.refs.totalUnitsDisplay).textContent = `${units} ${unitText}${coversText}`;
    /** @type {HTMLElement} */ (this.refs.totalCoverageDisplay).textContent =
      `${Math.round(coverage * 100) / 100} lin.in`;

    if (this.refs.overageDisplay) {
      if (this.overage > 0 && linFtWithOverage) {
        const overageLinFt = linFtWithOverage - originalLinFt;
        this.refs.overageDisplay.textContent = `${this.overage}% (+${Math.round(overageLinFt * 100) / 100} lin.in)`;
      } else {
        this.refs.overageDisplay.textContent = 'No overage';
      }
    }

    this.#updateAddToCartPrice(units);
  }

  #clearCalculations() {
    const coversText = this.covers > 0 ? ` (${this.covers} lin.in each)` : '';
    /** @type {HTMLElement} */ (this.refs.totalUnitsDisplay).textContent = `1 piece${coversText}`;
    /** @type {HTMLElement} */ (this.refs.totalCoverageDisplay).textContent =
      this.covers > 0 ? `${this.covers} lin.in` : '0 lin.in';

    if (this.errorElement) this.errorElement.style.display = 'none';
    this.#updateAddToCartPrice(0);
  }

  /** @param {number} percentage */
  #setOverage(percentage) {
    this.overage = parseFloat(String(percentage)) || 0;

    const linFtInput = /** @type {HTMLInputElement} */ (this.refs.linFtInput);
    const unitsInput = /** @type {HTMLInputElement} */ (this.refs.unitsInput);

    if (this.#originalLinFt > 0) {
      const linFtWithOverage = this.#originalLinFt * (1 + this.overage / 100);
      const piecesNeeded = this.covers > 0 ? Math.ceil(linFtWithOverage / this.covers) : 0;

      this.#isUpdating = true;

      const newLinFtValue = String(Math.round(linFtWithOverage * 100) / 100);
      linFtInput.value = newLinFtValue;
      linFtInput.setAttribute('value', newLinFtValue);

      const newUnitsValue = String(piecesNeeded);
      unitsInput.value = newUnitsValue;
      unitsInput.setAttribute('value', newUnitsValue);

      this.#isUpdating = false;

      unitsInput.dispatchEvent(new Event('input', { bubbles: true }));

      if (this.refs.unitsLabel) {
        this.refs.unitsLabel.textContent = piecesNeeded === 1 ? 'piece' : 'pieces';
      }

      const totalCoverage = piecesNeeded * this.covers;
      this.#updateDisplays(piecesNeeded, totalCoverage, this.#originalLinFt, linFtWithOverage);
    } else if (linFtInput?.value) {
      const mockEvent = { target: linFtInput };
      this.#handleLinFtChange(/** @type {Event} */ (/** @type {unknown} */ (mockEvent)));
    } else if (unitsInput?.value) {
      const mockEvent = { target: unitsInput };
      this.#handleUnitsChange(/** @type {Event} */ (/** @type {unknown} */ (mockEvent)));
    }
  }
}

if (!customElements.get('quantity-calculator-linear-inches')) {
  customElements.define('quantity-calculator-linear-inches', QuantityCalculatorPencilTrim);
}
